import { FlatStructuralTransformer } from '../services/transformers/FlatStructuralTransformer.js';
import { engineUtils } from '../services/EngineUtils.js';
import { SinglePropertyMutator } from '../internal/SinglePropertyMutator.js';
import { TransactionalMutator } from '../internal/TransactionalMutator.js';
import { CompatibilityFilter } from '../compatibility/CompatibilityFilter.js';
import { CSPUtils } from '../compatibility/CSPUtils.js';
import { CSPSolver } from '../compatibility/CSPSolver.js';
import { LookupMutatorCorrectionEventBuilder } from '../services/notifications/LookupMutatorCorrectionEvent.js';
import { extendedSinglePropertyLookupTypeGuard, multiPropertiesLookupRecordTypeGuard, singlePropertyLookupTypeGuard } from '../lookup/ResolutionSchemaDefinition.js';
export class LookupMutator {
    constructor(lookupId, value) {
        this.lookupId = lookupId;
        this.value = value;
    }
    mutate(context, typeRef, model) {
        var _a;
        const propertyPath = this.lookupId;
        const compatibilityInvolvedPaths = context.compatibilityService.getCompatibilityInvolvedOptionPaths(model, typeRef);
        const resolutionSchema = context.getResolutionSchema(typeRef);
        const lookup = resolutionSchema.getLookupById(this.lookupId);
        if (!lookup) {
            throw new Error(`Cannot find lookup ${this.lookupId}`);
        }
        const { option: optionPath } = lookup;
        const resolutionInvolvedPaths = resolutionSchema.allOptions();
        if (!resolutionInvolvedPaths.includes(optionPath)) {
            throw new Error(`LookupMutator: ${typeRef} is not defined in resolution schema`);
        }
        let optionsPaths = compatibilityInvolvedPaths;
        if (!compatibilityInvolvedPaths.includes(optionPath)) {
            optionsPaths = [optionPath];
        }
        let properties;
        let keys = [];
        if (singlePropertyLookupTypeGuard(lookup)) {
            properties = [lookup.property];
        }
        else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
            properties = [lookup.properties[lookup.key]];
        }
        else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
            properties = Object.values(lookup.properties);
            keys = Object.keys(lookup.properties);
        }
        const optionsPotentialValues = new Map();
        const oldOptionsValues = new Map();
        for (let optionPath of optionsPaths) {
            optionsPotentialValues.set(optionPath, context.optionValuesByPath(model, optionPath, new FlatStructuralTransformer()));
            const optionValue = engineUtils.getPropertyPathValue(model, optionPath);
            oldOptionsValues.set(optionPath, optionValue);
        }
        const topFilters = resolutionSchema.topFiltersFor(propertyPath);
        CSPUtils.applyFiltersTo(context.schema, topFilters, optionsPotentialValues, model);
        let currentOptionValues = (_a = optionsPotentialValues.get(optionPath)) !== null && _a !== void 0 ? _a : context.optionValuesByPath(model, optionPath, new FlatStructuralTransformer());
        const filteredCurrentOptionValues = [];
        for (let option of currentOptionValues) {
            const value = engineUtils.getPropertyPathValues(option.model, properties);
            let targetValue = this.value;
            let obj = {};
            if (singlePropertyLookupTypeGuard(lookup)) {
                obj = value[0];
            }
            else if (extendedSinglePropertyLookupTypeGuard(lookup)) {
                obj = value[0];
                targetValue = this.value[lookup.key];
            }
            else if (multiPropertiesLookupRecordTypeGuard(lookup)) {
                obj = engineUtils.buildObjectByKeys(value, keys);
            }
            if (engineUtils.equals(obj, targetValue)) {
                filteredCurrentOptionValues.push(option);
            }
        }
        if (!filteredCurrentOptionValues.length) {
            throw new Error('Cannot find compatible option');
        }
        let pickedOptions;
        optionsPotentialValues.set(optionPath, filteredCurrentOptionValues);
        const iterations = resolutionSchema.orderOptionValues(this.lookupId, model, this.value, optionsPotentialValues);
        const compatibilityFilter = new CompatibilityFilter(model, context.compatibilityService);
        for (const optionsValuesEntries of iterations) {
            compatibilityFilter.resetAll();
            const index = optionsValuesEntries.findIndex(entry => entry[0] === optionPath);
            let values;
            if (index >= 0) {
                values = optionsValuesEntries[index][1];
                optionsValuesEntries.splice(index, 1);
            }
            new CSPSolver(compatibilityFilter, values, optionsValuesEntries)
                .solve(optionPath, (option, options, q) => {
                pickedOptions = options;
                return 'Exit';
            });
            if (pickedOptions) {
                break;
            }
        }
        const mutators = [];
        if (!pickedOptions) {
            throw new Error(`Cannot mutate ${propertyPath} with value ${this.value}`);
        }
        const eventBuilder = new LookupMutatorCorrectionEventBuilder(optionPath, properties, resolutionSchema);
        for (let [path, option] of pickedOptions) {
            const oldOption = oldOptionsValues.get(path);
            if (oldOption === undefined || option._id != oldOption._id) {
                eventBuilder.appendDiff(path, oldOption, option.model);
                mutators.push(new SinglePropertyMutator(path, option));
            }
        }
        const [newModel, validationResult] = new TransactionalMutator(mutators).mutate(context, typeRef, model);
        if (eventBuilder.isAnyDiff()) {
            context.notifications.notify(LookupMutator.CorrectionEvent, eventBuilder.build(newModel, lookup));
        }
        return [newModel, validationResult];
    }
}
LookupMutator.CorrectionEvent = 'LookupMutatorCorrection';
//# sourceMappingURL=LookupMutator.js.map